import React, { useCallback, useMemo } from 'react'
import { useDevice } from 'device'
import { track } from 'analytics'
import { useCatalogueId, useCatalogueProducts } from 'modules/products'

import FetchMoreButtonProductList, { type FetchMoreButtonProductListProps } from 'compositions/productLists/FetchMoreButtonProductList/FetchMoreButtonProductList'

import messages from './messages'


export type ProductListProps = {
  catalogueName: ProductsModule.CatalogueName
  buttonProps: FetchMoreButtonProductListProps['buttonProps']
  isVisible: boolean
  limit?: number
  totalLimit?: number
  fetchMoreButtonTitle?: FetchMoreButtonProductListProps['fetchMoreButtonTitle']
  fetchMoreButtonStyle?: FetchMoreButtonProductListProps['fetchMoreButtonStyle']
  withDetailedMobileCards?: boolean
  withoutLink?: boolean
  withRetailPrice?: boolean
}

const ProductList: React.FunctionComponent<ProductListProps> = (props) => {
  const {
    catalogueName, buttonProps, isVisible, fetchMoreButtonTitle, fetchMoreButtonStyle,
    withDetailedMobileCards, withoutLink, withRetailPrice = false, limit, totalLimit = 72,
  } = props
  const { catalogueId, isFetching: isFetchingCatalogue } = useCatalogueId({ catalogueName })

  const { isMobile } = useDevice()

  const productsLimit = limit || (isMobile ? 12 : 6)

  const variables = useMemo<ProductsModule.UseCatalogueProductsProps['variables']>(() => ({
    id: catalogueId,
    input: {
      limit: productsLimit,
      offset: 0,
      productsFeed: 'PICK_ORDER_BRAND_PARTNERS',
    },
    withCount: true,
    withRetailPrice,
  }), [ catalogueId, productsLimit, withRetailPrice ])

  const { data, isFetching, fetchMore } = useCatalogueProducts({
    variables,
    ssr: false,
    notifyOnNetworkStatusChange: true,
    skip: !catalogueId || !isVisible,
  })

  const handleFetchMore = useCallback(() => {
    track('Show more products click', {
      placement: 'Bestsellers',
    })
    fetchMore({
      variables: {
        ...variables,
        input: {
          ...variables.input,
          offset: data?.products?.length,
        },
      },
    })
  }, [ data?.products?.length, fetchMore, variables ])

  const totalCount = Math.min(data?.count, totalLimit)

  return (
    <FetchMoreButtonProductList
      className="mt-24"
      products={data?.products}
      totalCount={totalCount}
      isFetching={!isVisible || isFetchingCatalogue || isFetching}
      fetchSkeletonsAmount={productsLimit}
      fetchMoreButtonTitle={fetchMoreButtonTitle || messages.buttonTitle}
      fetchMoreButtonStyle={fetchMoreButtonStyle || 'secondary'}
      buttonProps={buttonProps}
      fetchMore={handleFetchMore}
      withoutLink={withoutLink}
      withRetailPrice={withRetailPrice}
      withDetailedMobileCards={withDetailedMobileCards}
      excludeMarketingLabel="BESTSELLER"
    />
  )
}


export default ProductList
